import React from "react";
import hoistNonReactStatics from "hoist-non-react-statics";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { useDrop } from "react-dnd";
import InfiniteScroll from "react-infinite-scroll-component";

import {
  styled,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem,
  Stack,
  Backdrop,
} from "@mui/material";
import { DotsVertical as MenuIcon } from "mdi-material-ui";

import Layout from "components/common/Layout";
import OrderListItem from "./OrderListItem";

const ORDER_FRAGMENT = gql`
  fragment OrdersListState_order on Order {
    id
    deadline
    lastStateChange

    state {
      id
    }

    ...Orders_OrdersPageOrderListItem
  }

  ${OrderListItem.fragments.order}
`;

const ORDER_CONNECTION_FRAGMENT = gql`
  fragment OrdersListState_orderConnection on OrderConnection {
    count
    pageInfo {
      hasNextPage
    }
    edges {
      node {
        id

        ...OrdersListState_order
      }
    }
  }

  ${ORDER_FRAGMENT}
`;

const ORDER_STATE_FRAGMENT = gql`
  fragment OrdersListState_orderState on OrderState {
    id
    label
  }
`;

const MUTATION = gql`
  mutation ($orderId: ID!, $stateId: ID!) {
    UpdateOrderAdmin(orderId: $orderId, stateId: $stateId) {
      order {
        id

        ...OrdersListState_order
      }
    }
  }

  ${ORDER_FRAGMENT}
`;

const Count = styled(Box)(({ theme }) => ({
  height: 20,
  minWidth: 20,
  flexShrink: 0,
  padding: "0 6px",
  borderRadius: 10,
  lineHeight: 1,
  display: "flex",
  alignItems: "center",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));

function OrdersListState({
  orderState,
  onStateHide,
  onMoveAllOrders,
  onOrderDrop,
  onOrderDropRollback,
  loadNext,
}) {
  const [updateOrder, { loading }] = useMutation(MUTATION);

  const [{ showDropOverlay }, drop] = useDrop({
    accept: "order",
    drop: async (item, monitor) => {
      const prevStateId = item.order.state.id;
      const newStateId = orderState.id;

      // Pseudo optimistic UI update, mimic the server behavior
      const deadline =
        item.order.deadline ||
        new Date("3000-01-01T00:00:00.000Z").toISOString();
      const lastStateChange = new Date().toISOString();

      const order = {
        ...item.order,
        lastStateChange,
        priority: [deadline, lastStateChange].join("--"),
      };
      onOrderDrop(order, prevStateId, newStateId);

      updateOrder({
        variables: {
          orderId: item.order.id,
          stateId: orderState.id,
        },
      }).catch((err) => {
        onOrderDropRollback(err, item.order, prevStateId, newStateId);
      });
    },
    canDrop: (item, monitor) => item.order.state.id !== orderState.id,
    collect: (monitor, props) => ({
      showDropOverlay: monitor.canDrop() && monitor.isOver(),
    }),
  });

  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const handleMenuClick = React.useCallback((event) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);
  const handleMenuClose = React.useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleStateHide = React.useCallback(() => {
    setMenuAnchorEl(null);
    onStateHide(orderState);
  }, [orderState, onStateHide]);

  const handleMoveAllOrders = React.useCallback(() => {
    setMenuAnchorEl(null);
    onMoveAllOrders(orderState);
  }, [orderState, onMoveAllOrders]);

  const handleLoadNext = React.useCallback(() => {
    return loadNext(orderState);
  }, [loadNext, orderState]);

  return (
    <Box
      sx={{
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: 280,
        maxWidth: 280,
        minWidth: 280,
        display: "flex",
        flexDirection: "column",
        //   position: "relative",
        "& ~ &": { borderLeft: (t) => `1px solid ${t.palette.divider}` },
      }}
    >
      <Stack
        spacing={1}
        direction="row"
        alignItems="center"
        sx={{
          flexShrink: 0,
          p: (t) => t.spacing(0.5, 1),
          bgcolor: (t) => t.palette.background.odd,
        }}
      >
        <Count>{orderState.orders.count}</Count>

        <Typography variant="h4" color="primary" noWrap sx={{ flexGrow: 1 }}>
          {orderState.label}
        </Typography>

        {loading && <CircularProgress size="1em" />}

        <IconButton size="small" onClick={handleMenuClick}>
          <MenuIcon />
        </IconButton>

        <Menu
          anchorEl={menuAnchorEl}
          keepMounted
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleStateHide}>Masquer</MenuItem>
          <MenuItem onClick={handleMoveAllOrders}>Tout déplacer</MenuItem>
        </Menu>
      </Stack>

      <Box
        ref={drop}
        id={`OrderState-${orderState.id}-content`}
        sx={{ flexGrow: 1, overflow: "auto", position: "relative" }}
      >
        <InfiniteScroll
          scrollableTarget={`OrderState-${orderState.id}-content`}
          dataLength={orderState.orders.edges.length ?? 0}
          next={handleLoadNext}
          loader={<Layout.Loading />}
          hasMore={orderState.orders.pageInfo.hasNextPage}
          style={{
            overflow: "auto",
          }}
        >
          <Stack spacing={1} sx={{ p: 1 }}>
            {orderState.orders.edges.map(({ node }) => (
              <OrderListItem key={node.id} order={node} />
            ))}
          </Stack>
        </InfiniteScroll>

        <Backdrop open={showDropOverlay} sx={{ position: "absolute" }} />
      </Box>
    </Box>
  );
}

OrdersListState.fragments = {
  order: ORDER_FRAGMENT,
  orderConnection: ORDER_CONNECTION_FRAGMENT,
  orderState: ORDER_STATE_FRAGMENT,
};

export default hoistNonReactStatics(
  React.memo(OrdersListState),
  OrdersListState
);
